import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { getCompleteTicketData, clearCache } from '../services/api';

/**
 * useSharedTicketData Hook
 * Unified data provider that replaces useMapData, useKPIs, and useCompleteMapData
 * 
 * Features:
 * - Single API call to /getTickets using getAllTicketData()
 * - Multiple data transformations for all consuming components
 * - Maintains backward compatibility with existing interfaces
 * - Unified loading, error, and cache management
 * - Performance optimized with memoized transformations
 */
const useSharedTicketData = (refreshInterval = 3600000) => { // 1 hour default
  // Core state
  const [rawData, setRawData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  
  // KPI-specific state for change tracking
  const [previousKpiData, setPreviousKpiData] = useState(null);
  
  // Refs for cleanup and preventing memory leaks
  const mountedRef = useRef(true);
  const refreshTimeoutRef = useRef(null);
  const retryTimeoutRef = useRef(null);
  const intervalRef = useRef(null);
  const visibilityRef = useRef(true);

  // Time-based scheduler helper functions
  const calculateNextRefreshTime = useCallback(() => {
    const now = new Date();
    const nextRefresh = new Date(now);
    
    // Set to 15 minutes past the current hour
    nextRefresh.setMinutes(15, 0, 0);
    
    // If we're already past :15 of the current hour, move to next hour
    if (now.getMinutes() >= 15) {
      nextRefresh.setHours(nextRefresh.getHours() + 1);
    }
    
    return nextRefresh;
  }, []);

  const getMillisecondsUntilNextRefresh = useCallback(() => {
    const now = new Date();
    const nextRefresh = calculateNextRefreshTime();
    return nextRefresh.getTime() - now.getTime();
  }, [calculateNextRefreshTime]);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // Page visibility change handler
  useEffect(() => {
    const handleVisibilityChange = () => {
      const isVisible = !document.hidden;
      visibilityRef.current = isVisible;
      
      if (isVisible && mountedRef.current) {
        console.log('[useSharedTicketData] Page became visible, setting up refresh scheduler');
        setupTimeBasedRefresh();
      } else {
        console.log('[useSharedTicketData] Page became hidden, clearing refresh timers');
        clearRefreshTimers();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Clear all refresh timers
  const clearRefreshTimers = useCallback(() => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);


  // Country name to ISO code mapping (reused from useCompleteMapData)
  const getCountryCode = useCallback((countryName) => {
    const COUNTRY_NAME_TO_CODE = {
      'Italy': 'IT',
      'Germany': 'DE',
      'France': 'FR',
      'Spain': 'ES',
      'United Kingdom': 'GB',
      'Netherlands': 'NL',
      'Belgium': 'BE',
      'Austria': 'AT',
      'Switzerland': 'CH',
      'Poland': 'PL',
      'Portugal': 'PT',
      'Czech Republic': 'CZ',
      'Hungary': 'HU',
      'Slovakia': 'SK',
      'Slovenia': 'SI',
      'Croatia': 'HR',
      'Romania': 'RO',
      'Bulgaria': 'BG',
      'Greece': 'GR',
      'Denmark': 'DK',
      'Sweden': 'SE',
      'Norway': 'NO',
      'Finland': 'FI',
      'Ireland': 'IE',
      'Luxembourg': 'LU',
      'Malta': 'MT',
      'Cyprus': 'CY',
      'Estonia': 'EE',
      'Latvia': 'LV',
      'Lithuania': 'LT',
      'Canary Islands': 'ES',
      'Ceuta, Melilla': 'ES',
      'Holy See (Vatican City State)': 'VA',
      'San Marino': 'SM',
      'Monaco': 'MC'
    };
    
    return COUNTRY_NAME_TO_CODE[countryName] || countryName.substring(0, 2).toUpperCase();
  }, []);

  // Data validation helper (reused from useCompleteMapData)
  const validateApiData = useCallback((data) => {
    if (!data) return false;
    
    // Check required structure
    if (!data.summary || !data.by_location) {
      console.warn('[useSharedTicketData] Invalid data structure: missing summary or by_location');
      return false;
    }
    
    // Validate summary structure
    const requiredSummaryFields = ['total_appointments', 'total_in_progress', 'total_created_today', 'overall_in_progress_percentage'];
    for (const field of requiredSummaryFields) {
      if (typeof data.summary[field] !== 'number') {
        console.warn(`[useSharedTicketData] Invalid summary field: ${field}`);
        return false;
      }
    }
    
    return true;
  }, []);

  // Enhanced data fetching with error recovery
  const fetchData = useCallback(async (isRetry = false) => {
    if (!mountedRef.current) return;
    
    try {
      setLoading(true);
      if (!isRetry) {
        setError(null);
        setRetryCount(0);
      }
      
      console.log('[useSharedTicketData] Fetching ticket data...');
      const response = await getCompleteTicketData();
      
      // Validate the response data
      if (!validateApiData(response)) {
        throw new Error('Invalid API response structure');
      }
      
      if (mountedRef.current) {
        setRawData(response);
        setLastUpdated(response.last_updated || new Date().toISOString());
        setError(null);
        setRetryCount(0);
        
        const countryCount = Object.keys(response.by_location).length;
        console.log(`[useSharedTicketData] Successfully loaded data for ${countryCount} countries`);
      }
      
    } catch (err) {
      console.error('[useSharedTicketData] Error fetching data:', err);
      
      if (mountedRef.current) {
        setError({
          message: err.message || 'Failed to load ticket data',
          type: 'fetch_error',
          timestamp: new Date().toISOString(),
          canRetry: true
        });
        
        // Implement exponential backoff for retries
        setRetryCount(prev => {
          const currentRetryCount = isRetry ? prev : 0;
          if (currentRetryCount < 1) {
            const delay = Math.pow(2, currentRetryCount) * 2000; // 2s
            console.log(`[useSharedTicketData] Scheduling retry ${currentRetryCount + 1} in ${delay}ms`);
            
            retryTimeoutRef.current = setTimeout(() => {
              if (mountedRef.current) {
                fetchData(true);
              }
            }, delay);
            return currentRetryCount + 1;
          }
          return prev;
        });
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [validateApiData]);

  // Manual refresh with cache clearing
  const refetch = useCallback(async (clearCacheFirst = false) => {
    if (clearCacheFirst) {
      console.log('[useSharedTicketData] Clearing cache before refresh');
      clearCache();
    }
    
    await fetchData();
  }, [fetchData]);

  // Setup time-based refresh scheduler
  const setupTimeBasedRefresh = useCallback(() => {
    if (!mountedRef.current || !visibilityRef.current) return;
    
    // Clear any existing timers
    clearRefreshTimers();
    
    const msUntilNextRefresh = getMillisecondsUntilNextRefresh();
    const nextRefreshTime = calculateNextRefreshTime();
    
    console.log(`[useSharedTicketData] Setting up refresh scheduler - next refresh at ${nextRefreshTime.toLocaleTimeString()} (in ${Math.round(msUntilNextRefresh / 1000)}s)`);
    
    // Set timeout for the first refresh at :15 past the hour
    refreshTimeoutRef.current = setTimeout(() => {
      if (mountedRef.current && visibilityRef.current) {
        console.log('[useSharedTicketData] Time-based refresh triggered');
        fetchData();
        
        // Set up recurring interval for every hour after the first refresh
        intervalRef.current = setInterval(() => {
          if (mountedRef.current && visibilityRef.current) {
            console.log('[useSharedTicketData] Hourly refresh triggered');
            fetchData();
          }
        }, 60 * 60 * 1000); // 1 hour in milliseconds
      }
    }, msUntilNextRefresh);
  }, [getMillisecondsUntilNextRefresh, calculateNextRefreshTime, clearRefreshTimers, fetchData]);

  // Transform raw data to Map Data format (from useMapData)
  const mapData = useMemo(() => {
    if (!rawData || !rawData.by_location) {
      return { data: [], last_updated: lastUpdated };
    }

    const transformedData = [];
    
    Object.entries(rawData.by_location).forEach(([countryName, countryData]) => {
      const countryCode = getCountryCode(countryName);
      
      if (countryCode && countryData && typeof countryData.country_in_progress === 'number') {
        transformedData.push({
          country_code: countryCode,
          country_name: countryName,
          open_count: countryData.country_in_progress
        });
      }
    });

    return {
      data: transformedData,
      last_updated: lastUpdated || new Date().toISOString()
    };
  }, [rawData, lastUpdated, getCountryCode]);

  // Transform raw data to KPI Data format (from useKPIs)
  const kpiData = useMemo(() => {
    if (!rawData || !rawData.summary || !rawData.by_location) {
      return null;
    }

    const { summary, by_location } = rawData;

    // Extract total_open from summary.total_in_progress
    const total_open = summary.total_in_progress || 0;

    // Use reasonable default for avg_resolution_hours since it's not provided by Lambda
    const avg_resolution_hours = 48.5;

    // Calculate top_performer by finding country with lowest country_in_progress_percentage
    let topPerformer = { country: 'Unknown', closure_rate: 0 };
    let lowestPercentage = Infinity;

    Object.entries(by_location).forEach(([countryName, countryData]) => {
      if (countryData && typeof countryData.country_in_progress_percentage === 'number') {
        if (countryData.country_in_progress_percentage < lowestPercentage) {
          lowestPercentage = countryData.country_in_progress_percentage;
          topPerformer = {
            country: countryName,
            closure_rate: Math.round(countryData.country_in_progress_percentage * 10) / 10
          };
        }
      }
    });

    // Use summary data for other KPIs
    const sa_opened_today = summary.total_created_today || 0;
    const total_closed_today = summary.total_closed_today || 0;

    const kpiResult = {
      total_open,
      avg_resolution_hours,
      top_performer: topPerformer,
      sa_opened_today,
      total_closed_today,
      last_updated: lastUpdated || new Date().toISOString()
    };

    // Store previous KPI data for change tracking
    if (mountedRef.current) {
      setPreviousKpiData(currentPrevious => {
        // Only update if we have current KPI data and it's different
        if (currentPrevious && JSON.stringify(currentPrevious) !== JSON.stringify(kpiResult)) {
          return currentPrevious;
        }
        return currentPrevious;
      });
    }

    return kpiResult;
  }, [rawData, lastUpdated]);

  // Update previous KPI data when kpiData changes
  useEffect(() => {
    if (kpiData && mountedRef.current) {
      setPreviousKpiData(prev => {
        if (!prev) return null; // Don't set previous on first load
        return prev; // Keep existing previous data
      });
    }
  }, [kpiData]);

  // Transform raw data to Complete Map Data format (from useCompleteMapData)
  const countryMapData = useMemo(() => {
    if (!rawData || !rawData.by_location) return [];
    
    // Group by country code to handle multiple entries for the same country
    const countryMap = new Map();
    
    Object.entries(rawData.by_location).forEach(([countryName, countryData]) => {
      const countryCode = getCountryCode(countryName);
      
      if (countryMap.has(countryCode)) {
        // Merge data for the same country code
        const existing = countryMap.get(countryCode);
        const mergedCities = { ...existing.cities, ...(countryData.cities || {}) };
        
        countryMap.set(countryCode, {
          country_name: existing.country_name,
          country_code: countryCode,
          open_count: (existing.open_count || 0) + (countryData.country_in_progress || 0),
          total_count: (existing.total_count || 0) + (countryData.country_total || 0),
          in_progress_percentage: 0, // Will be recalculated below
          cities: mergedCities
        });
      } else {
        countryMap.set(countryCode, {
          country_name: countryName,
          country_code: countryCode,
          open_count: countryData.country_in_progress || 0,
          total_count: countryData.country_total || 0,
          in_progress_percentage: countryData.country_in_progress_percentage || 0,
          cities: countryData.cities || {}
        });
      }
    });
    
    // Recalculate percentages for merged countries
    return Array.from(countryMap.values()).map(country => ({
      ...country,
      in_progress_percentage: country.total_count > 0
        ? Math.round((country.open_count / country.total_count) * 100 * 100) / 100
        : 0
    }));
  }, [rawData, getCountryCode]);

  const cityMapData = useMemo(() => {
    if (!rawData || !rawData.by_location) return [];
    
    const cities = [];
    Object.entries(rawData.by_location).forEach(([countryName, countryData]) => {
      if (countryData.cities) {
        Object.entries(countryData.cities).forEach(([cityName, cityStats]) => {
          cities.push({
            id: `${getCountryCode(countryName)}-${cityName}`,
            name: cityName,
            country_name: countryName,
            country_code: getCountryCode(countryName),
            total: cityStats.total || 0,
            in_progress: cityStats.in_progress || 0,
            in_progress_percentage: cityStats.in_progress_percentage || 0
          });
        });
      }
    });
    
    return cities;
  }, [rawData, getCountryCode]);

  const storeLocationData = useMemo(() => {
    if (!rawData || !rawData.by_location) return [];
    
    const stores = [];
    
    Object.entries(rawData.by_location).forEach(([countryName, countryData]) => {
      if (countryData.stores && Object.keys(countryData.stores).length > 0) {
        Object.entries(countryData.stores).forEach(([coordKey, storeData]) => {
          // Extract coordinates from the key format "lat,lng"
          const [latStr, lngStr] = coordKey.split(',');
          const latitude = parseFloat(latStr);
          const longitude = parseFloat(lngStr);
          
          // Validate coordinates
          if (!isNaN(latitude) && !isNaN(longitude) && 
              latitude >= -90 && latitude <= 90 && 
              longitude >= -180 && longitude <= 180) {
            const store = {
              id: `${getCountryCode(countryName)}-${coordKey}`,
              coordinates: [latitude, longitude],
              latitude: storeData.latitude || latitude,
              longitude: storeData.longitude || longitude,
              store_name: storeData.store_name || '',
              country_name: countryName,
              country_code: getCountryCode(countryName),
              total: storeData.total || 0,
              in_progress: storeData.in_progress || 0,
              in_progress_percentage: storeData.in_progress_percentage || 0,
              coord_key: coordKey
            };
            stores.push(store);
          }
        });
      }
    });
    
    return stores;
  }, [rawData, getCountryCode]);

  const countryAggregatedData = useMemo(() => {
    if (!rawData || !rawData.by_location) return [];
    
    // Group by country code to handle multiple entries for the same country
    const countryMap = new Map();
    
    Object.entries(rawData.by_location).forEach(([countryName, countryData]) => {
      const countryCode = getCountryCode(countryName);
      
      if (countryMap.has(countryCode)) {
        // Merge data for the same country code
        const existing = countryMap.get(countryCode);
        
        countryMap.set(countryCode, {
          country_name: existing.country_name,
          country_code: countryCode,
          total_count: (existing.total_count || 0) + (countryData.country_total || 0),
          in_progress_count: (existing.in_progress_count || 0) + (countryData.country_in_progress || 0),
          in_progress_percentage: 0, // Will be recalculated below
          store_count: (existing.store_count || 0) + (countryData.stores ? Object.keys(countryData.stores).length : 0),
          stores: { ...(existing.stores || {}), ...(countryData.stores || {}) }
        });
      } else {
        countryMap.set(countryCode, {
          country_name: countryName,
          country_code: countryCode,
          total_count: countryData.country_total || 0,
          in_progress_count: countryData.country_in_progress || 0,
          in_progress_percentage: countryData.country_in_progress_percentage || 0,
          store_count: countryData.stores ? Object.keys(countryData.stores).length : 0,
          stores: countryData.stores || {}
        });
      }
    });
    
    // Recalculate percentages for merged countries and format for compatibility
    return Array.from(countryMap.values()).map(country => ({
      country_name: country.country_name,
      country_code: country.country_code,
      // Maintain compatibility with existing interface
      open_count: country.in_progress_count,
      total_count: country.total_count,
      in_progress_percentage: country.total_count > 0
        ? Math.round((country.in_progress_count / country.total_count) * 100 * 100) / 100
        : 0,
      // Enhanced data from real API
      store_count: country.store_count,
      stores: country.stores
    }));
  }, [rawData, getCountryCode]);

  const summaryData = useMemo(() => {
    return rawData?.summary || {
      total_appointments: 0,
      total_in_progress: 0,
      total_created_today: 0,
      overall_in_progress_percentage: 0
    };
  }, [rawData]);

  // KPI utility functions (from useKPIs)
  const getKPIChange = useCallback((key) => {
    if (!kpiData || !previousKpiData || !kpiData[key] || !previousKpiData[key]) {
      return null;
    }
    
    const current = kpiData[key];
    const previous = previousKpiData[key];
    
    // Handle nested objects (like top_performer)
    if (typeof current === 'object' && typeof previous === 'object') {
      if (key === 'top_performer') {
        const currentRate = current.closure_rate || 0;
        const previousRate = previous.closure_rate || 0;
        const change = currentRate - previousRate;
        
        return {
          absolute: change,
          percent: previousRate !== 0 ? (change / previousRate) * 100 : 0,
          isPositive: change > 0,
          countryChanged: current.country !== previous.country
        };
      }
      return null;
    }
    
    // Handle numeric values
    if (typeof current === 'number' && typeof previous === 'number') {
      const change = current - previous;
      const percentChange = previous !== 0 ? (change / previous) * 100 : 0;
      
      return {
        absolute: change,
        percent: percentChange,
        isPositive: change > 0
      };
    }
    
    return null;
  }, [kpiData, previousKpiData]);

  const formatKPIValue = useCallback((value, type, options = {}) => {
    if (value === null || value === undefined) {
      return 'N/A';
    }
    
    switch (type) {
      case 'count':
        return typeof value === 'number' ? value.toLocaleString() : '0';
        
      case 'hours':
        if (typeof value === 'number') {
          if (value < 1) {
            return `${Math.round(value * 60)}m`;
          } else if (value < 24) {
            return `${value.toFixed(1)}h`;
          } else {
            const days = Math.floor(value / 24);
            const hours = Math.round(value % 24);
            return `${days}d ${hours}h`;
          }
        }
        return '0h';
        
      case 'percentage':
        return typeof value === 'number' ? `${value.toFixed(1)}%` : '0%';
        
      case 'country':
        return value || 'N/A';
        
      case 'closure_rate':
        return typeof value === 'number' ? `${value.toFixed(1)}%` : '0%';
        
      default:
        return String(value);
    }
  }, []);

  const getFormattedKPI = useCallback((key, type, options = {}) => {
    if (!kpiData) return { value: 'N/A', change: null };
    
    const value = kpiData[key];
    const formattedValue = formatKPIValue(value, type, options);
    const change = getKPIChange(key);
    
    return {
      value: formattedValue,
      rawValue: value,
      change,
      hasChange: change !== null
    };
  }, [kpiData, formatKPIValue, getKPIChange]);

  // Utility functions from useMapData
  const getTotalOpenCount = useCallback(() => {
    return mapData.data.reduce((total, country) => total + (country.open_count || 0), 0);
  }, [mapData]);

  const getCountryData = useCallback((countryCode) => {
    return mapData.data.find(country => country.country_code === countryCode) || null;
  }, [mapData]);

  // Initial fetch effect
  useEffect(() => {
    fetchData();
  }, [fetchData]); // Include fetchData in dependencies

  // Setup time-based refresh scheduler effect
  useEffect(() => {
    // Set up the time-based scheduler when component mounts or becomes visible
    setupTimeBasedRefresh();
    
    return () => {
      clearRefreshTimers();
    };
  }, [setupTimeBasedRefresh]);

  // Computed values
  const nextRefreshTime = calculateNextRefreshTime();
  const msUntilNextRefresh = getMillisecondsUntilNextRefresh();
  const hasData = !!rawData;
  const isEmpty = !rawData && !loading && !error;

  return {
    // Map Data (from useMapData)
    mapData,
    
    // KPI Data (from useKPIs)  
    kpiData,
    previousKpiData,
    
    // Complete Map Data (from useCompleteMapData)
    countryMapData,
    cityMapData, 
    storeLocationData,
    countryAggregatedData,
    summaryData,
    
    // Shared State
    loading,
    error,
    lastUpdated,
    
    // Control Functions
    refetch,
    
    // Utility Functions (from useKPIs)
    formatKPIValue,
    getFormattedKPI,
    getKPIChange,
    
    // Utility Functions (from useMapData)
    getTotalOpenCount,
    getCountryData,
    
    // Computed values
    nextRefreshTime,
    msUntilNextRefresh,
    hasData,
    isEmpty,
    retryCount,
    
    // Debug info
    debugInfo: {
      mountedRef: mountedRef.current,
      isVisible: visibilityRef.current,
      nextRefreshTime: nextRefreshTime.toISOString(),
      msUntilNextRefresh,
      nextRefreshIn: `${Math.round(msUntilNextRefresh / 1000)}s`,
      cacheAge: lastUpdated ? Date.now() - new Date(lastUpdated).getTime() : null,
      hasRawData: !!rawData,
      hasPreviousKpiData: !!previousKpiData,
      countryCount: rawData ? Object.keys(rawData.by_location || {}).length : 0,
      cityCount: rawData ? Object.values(rawData.by_location || {}).reduce((total, country) =>
        total + (country.cities ? Object.keys(country.cities).length : 0), 0
      ) : 0,
      storeCount: storeLocationData.length,
      mapDataCount: mapData.data.length
    }
  };
};

export default useSharedTicketData;